import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import TextareaAutosize from "react-textarea-autosize"
import { FormattedMessage } from "gatsby-plugin-react-intl"
import { useField } from "formik"

import {
  InputContainer,
  InputFieldLabel,
  InputFieldIcon,
  ErrorMessage,
  DisabledMessage,
  HelpText,
} from "./common"

import CheckmarkSVG from "../../images/icons/check--small.inline.svg"
import CrossSVG from "../../images/icons/cross--small.inline.svg"

const TextareaField = styled(TextareaAutosize)`
  appearance: none;
  background: transparent;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid var(--color-sage);
  display: block;
  font-size: inherit;
  margin-top: 3px;
  padding: 3px 20px 3px 0;
  resize: none;
  line-height: inherit;
  width: 100%;

  ${(props) =>
    props.$error &&
    css`
      border-color: var(--color-error);
    `}

  &:focus {
    border-color: var(--color-sun);
    outline-offset: 0 !important;
  }

  &::placeholder {
    color: var(--color-sage);
    opacity: 0.75;
  }
`

const Textarea = ({
  className,
  disabled,
  forwardRef,
  hidden,
  hideLabel,
  hideRequired,
  label,
  maxLength,
  maxLengthCountdown,
  minLength,
  minLengthCountdown,
  minWords,
  onBlur,
  onFocus,
  placeholder,
  required,
  ...props
}) => {
  const [field, meta] = useField(props)
  const [active, setActive] = useState(false)
  const [ok, setOk] = useState(false)
  const fieldId = props.id || field.name
  const hasDisabledMessage = typeof disabled === "string"
  const hasError = meta.touched && meta.error

  useEffect(() => {
    if (field.value || placeholder) {
      setActive(true)
    }

    if (
      !field.value ||
      meta.error ||
      (minLength && field.value?.length < minLength) ||
      (maxLength && field.value?.length > maxLength)
    ) {
      setOk(false)
    } else {
      setOk(true)
    }
  }, [field.value, meta.error, placeholder, maxLength, minLength])

  const handleFocus = (callback) => {
    setActive(true)
    setOk(false)
    callback && callback()
  }

  const handleBlur = (callback) => {
    if (!field.value && !placeholder) {
      setActive(false)
    }
    if (
      (maxLength && field.value?.length <= maxLength && !meta.error) ||
      (minLength && field.value?.length >= minLength && !meta.error) ||
      (!maxLength && !minLength && field.value && !meta.error)
    ) {
      setOk(true)
    }
    callback && callback()
  }

  const describedByIds = [
    !disabled && !meta.error && maxLength && maxLengthCountdown
      ? `${fieldId}-helptext`
      : null,
    !disabled && !meta.error && minLength ? `${fieldId}-helptext` : null,
    hasDisabledMessage ? `${fieldId}-disabled` : null,
    hasError ? `${fieldId}-error` : null,
    props["aria-describedby"] || null,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <InputContainer
      className={className}
      data-testid={`textarea-${fieldId}`}
      hidden={hidden}
    >
      <InputFieldLabel
        $active={active}
        hidden={hideLabel}
        htmlFor={fieldId}
        id={`${fieldId}-label`}
      >
        {label} {required && !hideRequired && "*"}
      </InputFieldLabel>
      <TextareaField
        {...field}
        {...props}
        aria-describedby={describedByIds || undefined}
        aria-invalid={hasError ? "true" : undefined}
        aria-labelledby={`${fieldId}-label`}
        aria-required={required}
        disabled={disabled}
        $error={hasError ? "true" : undefined}
        id={fieldId}
        maxLength={maxLength}
        onBlur={() => handleBlur(onBlur)}
        onFocus={() => handleFocus(onFocus)}
        placeholder={
          placeholder &&
          `${placeholder}${required && hideLabel && !hideRequired ? " *" : ""}`
        }
        ref={forwardRef}
      />
      {ok && (
        <InputFieldIcon data-testid="checkmark">
          <CheckmarkSVG aria-hidden="true" />
        </InputFieldIcon>
      )}
      {hasError ? (
        <>
          {meta.error && (
            <InputFieldIcon $error data-testid="errorcross">
              <CrossSVG aria-hidden="true" />
            </InputFieldIcon>
          )}
          <ErrorMessage id={`${fieldId}-error`} role="alert">
            {meta.error}
            {hasDisabledMessage && (
              <DisabledMessage id={`${fieldId}-disabled`}>
                {disabled}
              </DisabledMessage>
            )}
          </ErrorMessage>
        </>
      ) : null}
      {!disabled && !meta.error && maxLength && maxLengthCountdown ? (
        <HelpText
          $error={meta.value?.length === maxLength}
          $fixedPosition="right"
          data-testid={`${fieldId}-helptext`}
          id={`${fieldId}-helptext`}
        >
          {meta.value?.length >= maxLength - maxLengthCountdown ? (
            <FormattedMessage
              id="forms.maxCharsLeft"
              values={{
                chars: maxLength - meta.value?.length,
              }}
            />
          ) : (
            <FormattedMessage
              id="forms.maxChars"
              values={{
                chars: maxLength,
              }}
            />
          )}
        </HelpText>
      ) : null}
      {!disabled && !meta.error && minLength ? (
        <HelpText
          $fixedPosition="right"
          data-testid={`${fieldId}-helptext`}
          id={`${fieldId}-helptext`}
        >
          {minLengthCountdown &&
          meta.value?.length >= minLengthCountdown &&
          meta.value?.length < minLength ? (
            <FormattedMessage
              id="forms.minCharsLeft"
              values={{
                chars: minLength - meta.value?.length,
              }}
            />
          ) : meta.value?.length < minLength ? (
            <FormattedMessage
              id={`${
                minWords ? "forms.minCharsAndWordsFriendly" : "forms.minChars"
              }`}
              values={{
                chars: minLength,
                words: minWords,
              }}
            />
          ) : null}
        </HelpText>
      ) : null}
      {hasDisabledMessage && !meta.error && (
        <DisabledMessage
          data-testid={`${fieldId}-disabled`}
          id={`${fieldId}-disabled`}
        >
          {disabled}
        </DisabledMessage>
      )}
    </InputContainer>
  )
}

Textarea.propTypes = {
  "aria-describedby": PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  forwardRef: PropTypes.object,
  hidden: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hideRequired: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  maxLengthCountdown: PropTypes.number,
  minLength: PropTypes.number,
  minLengthCountdown: PropTypes.number,
  minWords: PropTypes.number,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
}

export default Textarea
